/**
 * The Command Deck: the second dashboard shape, and the one he approved as a prototype.
 *
 * A SEPARATE surface rather than an edit to the tracking tower, at his instruction. The two
 * answer the same question in different shapes and he wants both, switchable, so neither is
 * a compromise. The tower puts twelve panels in three tiers and is what he reads when he
 * intends to work through the board; the deck leads with the persona and three numbers, keeps
 * the priority signal in view, and folds everything else into six modules he opens on purpose.
 *
 * Three rules held while porting it:
 *
 * 1. NOTHING IS LOST. Every panel on the tower is reachable here, inside the module it
 *    belongs to. The modules render the SAME components — `PipelinePanel`, `WeeklyOpsPanel`,
 *    `HealthPanel`, `ChangesPanel`, `Telemetry` and the rest — imported, not reimplemented.
 *    Two copies of "what counts as a commitment" would drift, and the point of a second shape
 *    is that it shows the same facts.
 *
 * 2. NO NUMBER IS WRITTEN IN. The prototype had plausible figures typed into it. Every one
 *    here is counted from the payload, including the CAPPED remainders: `keyActions` is
 *    hard-capped and `proposals` caps at three, so a module header reading the array length
 *    alone would say "3" on a morning with twenty.
 *
 * 3. THE PROTOTYPE'S OWN FICTION IS NOT SHIPPED. It carried a Projects/Missions module with
 *    progress bars, a waveform and a mic button. MO OS has no source for a project's percentage
 *    complete, and no speech input exists. Rendering them would put invented numbers on the one
 *    surface whose value is being trustworthy, so the module says what it is waiting for and the
 *    mic is absent rather than dead. This is the one place the deck deliberately departs from the
 *    picture, and it departs by admitting a gap rather than filling it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAssurance } from './useAssurance.js';
import { VIEW_ROWS, type DeckView } from './views.js';
import type { Dashboard, DashboardKeyAction } from '@rbv/shared';
import { BUILD_ID } from '../build.js';
import { Icon, type IconName } from './Icon.js';
import { DeckDetail, type DeckDetailItem } from './DeckDetail.js';
import { BriefingButton } from './Briefing.js';
import { Aurebesh } from './Aurebesh.js';
import { Panel } from './Panel.js';
import { Brain } from './Brain.js';
import { CoreNucleus } from './CoreNucleus.js';
import { StackedBar } from './Chart.js';
import { ChangesPanel } from './Changes.js';
import { ChaseRow } from './Chase.js';
import { HealthStrip } from './Forge.js';
import { HealthPanel } from './Health.js';
import { PipelinePanel } from './Pipeline.js';
import { SocialPanel } from './SocialPanel.js';
import { UntouchedPanel } from './Untouched.js';
import { WeeklyOpsPanel } from './WeeklyOps.js';
import { DailyBrief } from './DailyBrief.js';
import { AnnunciatorStrip } from './Annunciators.js';
import { ChatLauncher, openAsk } from './ChatOverlay.js';
import { ChatBar } from './ChatBar.js';
import { SinceYouChecked } from './SinceYouChecked.js';
import { closeCommitment } from './api.js';
import { useApp } from '../store.js';
import {
  AgendaList,
  CommitmentList,
  GapsBlock,
  IntentBlock,
  KeyActionList,
  NextMeeting,
  ProposalsBlock,
  StuckList,
  Telemetry,
  UpdateList,
  greeting,
  longDate,
  useReveal,
  whenLabel,
} from './MoDashboard.js';
import './commandDeck.css';

/**
 * Take him to a section of this board.
 *
 * The deck's sections are panels, not routes, so "go to Relationships" means open that panel and
 * put it in front of him. Three things happen, and all three are needed: the `<details>` is opened
 * (scrolling to a collapsed summary lands on a closed box and reads as nothing having happened),
 * it is scrolled into its own column rather than the page (`block: 'nearest'` walks the scroll
 * container the panels actually live in), and it is marked for a moment so the eye can find where
 * it went. Without the mark a jump inside a column of eight similar rows is invisible.
 *
 * `prefers-reduced-motion` drops the smooth scroll but keeps the arrival and the mark.
 */
function focusPanel(id: string): void {
  const el = document.getElementById(id);
  if (el === null) return;
  if (el instanceof HTMLDetailsElement) el.open = true;
  /*
   * ORDER MATTERS, and it took a test failure to see it. The scroll used to run first, and
   * `scrollIntoView` is the one call here that can be absent: jsdom has no implementation, and
   * neither do some embedded webviews. It threw, the handler died, and the arrival mark was never
   * set, so the link would have opened a panel and then looked broken again in exactly the
   * environments least able to report it. Do the two things that must happen first, then scroll,
   * guarded.
   */
  el.setAttribute('data-arrived', 'true');
  globalThis.setTimeout(() => el.removeAttribute('data-arrived'), 1400);
  const still = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  if (typeof el.scrollIntoView === 'function') {
    el.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' });
  }
}

/** The rail's sections. `count` is resolved from the payload, never written in. */
type RailItem = {
  icon: IconName;
  label: string;
  caption: string;
  /**
   * WHERE IT GOES. Exactly one of these, and no item may have none.
   *
   * Six of the eight used to carry `target: ''` against a handler that read
   * `if (it.target !== '') reveal(...)`, so Attention, Relationships, Knowledge, Agents, System
   * and Home did nothing at all when pressed. This file already argues, about the topbar, that a
   * row of controls where nothing happens is worse than no row: he presses one, nothing happens,
   * and every other control on the board becomes suspect. The rail never got that treatment.
   *
   * The honest destinations were here the whole time. These sections are not separate pages, they
   * are panels on this board, so the rail opens the panel and takes him to it. Knowledge is the
   * exception because the archives really are their own view.
   */
  /** A DOM id on this board: open it, scroll to it, and mark it so the jump is visible. */
  panel?: string;
  /** A vault path or title for `reveal`. */
  target?: string;
  /** The archives, which is a real view rather than a panel on this board. */
  archives?: boolean;
  count?: number;
  /** Marked as where he is. The mockup marks HOME; a rail with no current item is a list. */
  current?: boolean;
};

export function CommandDeck({
  dashboard: d,
  railOpen,
  onToggleRail,
  onReload,
  onResolve,
  onPickView,
}: {
  dashboard: Dashboard;
  railOpen: boolean;
  onToggleRail: () => void;
  onReload: () => void;
  onResolve: (id: number) => Promise<void>;
  /** Any of the four views. The switch is his, so it is always one press away. */
  onPickView: (v: DeckView) => void;
}): JSX.Element {
  /*
   * The row he opened, or null. One piece of state for the whole board: only one panel is ever
   * open, and holding it here rather than in each row means Escape and the backdrop close
   * whatever is showing without every row needing its own copy of that.
   */
  const [detail, setDetail] = useState<DeckDetailItem | null>(null);

  const reveal = useReveal();
  // Knowledge is a view, not a panel, so the rail needs the router as well as the anchors.
  const openView = useApp((st) => st.openView);

  /* The lanes, polled. See useAssurance.ts for why it is separate from the payload. */
  const assurance = useAssurance();

  const decisions = d.proposals.length + d.proposalsMore;
  const actions = d.keyActions.length + d.keyActionsMore;
  /* The next meeting he could PREPARE for. A recurring standup earns no pre-read, and one
     sitting here would make the deck say "next up: standup" every morning. */
  const next = d.agenda.find((m) => !m.recurring) ?? null;
  const commitments = d.lanes.due + d.lanes.live;
  /* `you-owe` means the next move is his. The other three directions are theirs, mutual, or
     unknown, and lumping `unknown` in with his would overstate what he actually owes. */
  const accountsWaiting = (d.pipeline ?? []).filter((a) => a.direction === 'you-owe').length;
  /* Absent means the CRM did not answer and an empty array means nothing is owed, so this `?? []`
     is only ever read for LENGTH and for slicing. Every place that must tell the two apart reads
     `d.chases` directly rather than this. */
  const chases = d.chases ?? [];

  /*
   * The mockup's own sections and captions, in its order.
   *
   * HOME is first and marked current, because the mockup marks it and because a rail with no
   * current item does not tell him where he is. PROJECTS is here for the same reason the module
   * is: it is a real section of his work even though MO OS holds no progress figure for it.
   *
   * Counts are resolved from the payload or omitted. HOME and PROJECTS carry none: "how many
   * dashboards" is not a number, and a project count would have to be invented.
   */
  const rail: RailItem[] = [
    { icon: 'graph', label: 'Home', caption: 'Command deck', panel: 'deck-top', current: true },
    /* Chases join the total because a re-approach he owes an account needs him exactly as much as
       a commitment does, and a rail count that omits them says the morning is quieter than it is. */
    /*
     * THE CAPTION CARRIES THE REGISTER, THE LABEL STAYS PLAIN.
     *
     * `Projects / Missions` already worked this way before anyone planned a language layer, and it
     * is the right shape: the label is what he scans at 6am and "Today" cannot be improved on, so
     * the second line is where the ship's vocabulary lives. Every rename has to be clearer than or
     * equal to what it replaces, which is why `Needs you` and `Accounts & people` are untouched:
     * they are already the best words available.
     */
    { icon: 'judgement', label: 'Attention', caption: 'Needs you', panel: 'deck-attention', count: decisions + actions + chases.length },
    { icon: 'agenda', label: 'Today', caption: 'Flight plan', panel: 'deck-today', count: d.agenda.length },
    { icon: 'waiting', label: 'Relationships', caption: 'Accounts & people', panel: 'deck-relationships', count: d.pipeline?.length },
    { icon: 'objective', label: 'Projects', caption: 'Missions', panel: 'deck-objectives' },
    { icon: 'notes', label: 'Knowledge', caption: 'The archives', archives: true, count: d.holdings.totalNotes },
    { icon: 'agent', label: 'Agents', caption: 'The crew', panel: 'deck-crew' },
    { icon: 'action', label: 'System', caption: 'The bridge', panel: 'deck-system', count: d.limitations.length },
  ];

  return (
    <main className="mo-dash cdeck" data-rail={railOpen ? 'open' : 'closed'}>
      <button
        type="button"
        className="cdeck-railtoggle"
        aria-expanded={railOpen}
        aria-label={railOpen ? 'Collapse the menu' : 'Expand the menu'}
        onClick={onToggleRail}
      >
        <Icon name="chevron" size={12} />
      </button>

      <aside className="cdeck-rail" aria-label="Deck sections">
        <div className="cdeck-brand">
          <img src="/deck-emblem.webp" alt="" width={44} height={44} aria-hidden="true" />
          <span>
            <span className="cdeck-brand-name">MO OS</span>
            <span className="cdeck-brand-sub">
              Personal
              <br />
              intelligence system
            </span>
          </span>
        </div>
        {/* Frame furniture. Decoration by his instruction, so it spells nothing and sits where
            nothing has to be read; see mo/Aurebesh.tsx on why it takes a seed and not text. */}
        <Aurebesh seed="mo-os-rail" length={9} size={13} />
        {/* Hull stencil down the rail's edge, running bottom-to-top the way real plating is
            marked. Absolutely positioned so it cannot push the nav around. */}
        <Aurebesh className="cdeck-rail-stencil" seed="mo-os-hull-plate" length={14} size={11} />

        <nav className="cdeck-nav">
          {rail.map((it) => (
            <button
              key={it.label}
              type="button"
              className="cdeck-nav-item"
              data-current={it.current === true}
              aria-current={it.current === true ? 'page' : undefined}
              title={`${it.label} — ${it.caption}`}
              onClick={() => {
                if (it.archives === true) {
                  openView({ kind: 'mo' });
                  return;
                }
                if (it.panel !== undefined) {
                  focusPanel(it.panel);
                  return;
                }
                if (it.target !== undefined && it.target !== '') reveal(it.target);
              }}
            >
              <Icon name={it.icon} size={17} />
              <span>
                <span className="cdeck-nav-label">{it.label}</span>
                <span className="cdeck-nav-caption">{it.caption}</span>
              </span>
              {/* Absent, not zero: a section with no honest number shows none. */}
              {it.count !== undefined && <span className="cdeck-nav-count">{it.count}</span>}
            </button>
          ))}
        </nav>

        <div className="cdeck-rail-foot">
          <div className="cdeck-owner">
            <span className="cdeck-owner-av">
              <img src="/crew-hermes.webp" alt="" width={216} height={162} aria-hidden="true" />
            </span>
            <span>
              <span className="cdeck-owner-name">MO</span>
              <span className="cdeck-owner-role">System owner</span>
            </span>
          </div>
          {/*
            A PICKER, NOT A TOGGLE, and that was a real gap rather than a preference.
            There are three MO OS surfaces: this deck, the tracking tower, and the brain's own
            page. The control was a single toggle whose only destination was the tower, so from
            anywhere else the MAIN view was not something you could choose, only somewhere you
            ended up. It now lists all three and names the one you are on, because a switcher that
            hides the current view leaves you guessing where you are.
          */}
        </div>
      </aside>

      {/* ---- top bar ---------------------------------------------------- */}
      <header className="cdeck-top" id="deck-top">
        <div className="cdeck-hello">
          <h1>
            {greeting()} <span className="cdeck-spark" aria-hidden="true">&#10022;</span>
          </h1>
          <p>
            {longDate(d.generatedAt)}
            <span className="cdeck-when">{whenLabel(d.generatedAt)}</span>
          </p>
        </div>

        <p className="cdeck-motto">
          <b>Clarity comes from signal.</b>
          <span>Focus. Decide. Move.</span>
        </p>

        <div className="cdeck-topright">
          {/*
            IN THE TOP BAR, NOT THE RAIL FOOT, and that move is a bug fix rather than a preference.
            The rail scrolls: its content runs to about 1030px, so on any window shorter than that
            the foot sits below the fold and the switch is unreachable. It was only ever visible on
            a tall display. The wall and the work board both keep it top right, so this also makes
            the four views agree on where it lives.
          */}
          <ViewPicker current="deck" onPick={onPickView} />
          <SystemLight limitations={d.limitations} />
          {/*
            The five controls from the mockup. Each one GOES somewhere, because a row of
            decorative buttons on a dashboard is worse than none: he presses one, nothing
            happens, and every other control on the board becomes suspect.
          */}
          <span className="cdeck-icons">
            <IconButton label="What needs you" icon="action" onClick={() => reveal('commitment')} />
            <IconButton label="Needs a decision" icon="judgement" onClick={() => reveal('')} />
            <IconButton label="Your calendar" icon="agenda" onClick={() => reveal('meeting')} />
            <IconButton label="The vault" icon="notes" onClick={() => reveal('')} />
            <IconButton
              label="Ask MO OS"
              icon="graph"
              medal
              onClick={() => openAsk({ lane: 'brain', about: 'what needs me today' })}
            />
          </span>
        </div>
      </header>

      {/*
        ---- assurance ---------------------------------------------------
        ABOVE the hero, not below it. A warning placed under the figures it qualifies is read
        second, by which point the figures have already been believed.
      */}
      {assurance !== null && <HealthStrip view={assurance} />}

      {/* ---- hero ------------------------------------------------------- */}
      <section className="cdeck-hero" aria-label="MO OS">
        <img className="cdeck-vista" src="/deck-vista.webp" alt="" aria-hidden="true" />
        {/*
          A stencil band across the hero's foot. This is the placement that answers "I don't see
          Aurebesh at all": the rail strip is nine glyphs in a 200px column and the eye never goes
          there, whereas the hero is the widest thing on the board and its bottom edge is empty
          plate. Decoration by his instruction, so it spells nothing.
        */}
        <Aurebesh className="cdeck-hero-stencil" seed="hero-plate-band" length={20} size={12} />
        {/*
          Ships at distance. Every reference board he sent has them and the hero did not, which is
          most of why ours read as a portrait on a wall rather than a view out of somewhere. They
          are DEPTH, not content: far back, dim, behind the mask the vista already carries, and
          they never move, because the rule that nothing animates where he reads still holds and
          this band is the first thing he looks at. The graph is where the sky moves.
        */}
        <img className="cdeck-hero-ship" data-lane="far" src="/ship-freighter.webp" alt="" aria-hidden="true" />
        <img className="cdeck-hero-ship" data-lane="mid" src="/ship-fighter.webp" alt="" aria-hidden="true" />
        <img className="cdeck-hero-ship" data-lane="near" src="/ship-interceptor.webp" alt="" aria-hidden="true" />

        <div className="cdeck-hero-words">
          <p className="cdeck-wordmark">MO OS</p>
          <p className="cdeck-tagline">Your personal operating system</p>
          <p className="cdeck-creed">
            I monitor, connect and remind.
            <br />
            You decide, I execute.
          </p>
          <button
            type="button"
            className="cdeck-talk"
            onClick={() => openAsk({ lane: 'brain', about: 'what needs me today' })}
          >
            <span className="cdeck-spark" aria-hidden="true">&#10022;</span> Talk to MO
          </button>
        </div>

        {/* The rings and the wordmark are drawn OVER the plate rather than generated into it:
            text baked into an image cannot be kept crisp, and a generator will eventually
            misspell it. */}
        <span className="cdeck-figw">
          <svg viewBox="0 0 310 310" aria-hidden="true">
            <circle cx="155" cy="155" r="132" fill="none" stroke="currentColor" opacity="0.16" />
            <circle
              cx="155"
              cy="155"
              r="106"
              fill="none"
              stroke="currentColor"
              opacity="0.09"
              strokeDasharray="2 9"
            />
          </svg>
          <img className="cdeck-figure" src="/deck-mo.webp" alt="" width={460} height={460} />
          <span className="cdeck-mark" aria-hidden="true">
            MO
          </span>
        </span>

        <div className="cdeck-stats">
          <HeroStat
            tone="judgement"
            icon="judgement"
            value={String(decisions)}
            label={decisions === 1 ? 'Decision' : 'Decisions'}
            note="need your judgement"
            onClick={() => reveal('')}
          />
          <HeroStat
            tone="action"
            icon="action"
            value={String(actions)}
            label={actions === 1 ? 'Action' : 'Actions'}
            note="need your attention"
            onClick={() => reveal('commitment')}
          />
          <HeroStat
            tone="time"
            icon="clock"
            value={next === null ? 'Nothing to prepare' : `Next up ${meetingWhenLabel(next)}`}
            label={next?.title ?? 'No one-off meeting ahead'}
            note={
              next === null
                ? 'recurring series are not counted'
                : next.organiser !== ''
                  ? next.organiser
                  : 'no organiser recorded'
            }
            onClick={() => reveal(next?.title ?? 'meeting')}
            wide
          />
        </div>
      </section>

      {/* ---- priority signal ------------------------------------------- */}
      <div className="cdeck-col cdeck-col-signal" id="deck-attention">
        <p className="cdeck-coltitle">
          <span className="cdeck-spark" aria-hidden="true">&#10022;</span>
          Priority signal
          <button type="button" className="cdeck-viewall" onClick={() => reveal('commitment')}>
            View all ({actions + decisions})
          </button>
          <Aurebesh seed="col-signal" length={5} size={10} />
        </p>
        {/*
          THE CHARACTER IS TIED TO THE STATE, NOT TO THE PAGE.
          He is here when something is past its due date and absent when nothing is, so his
          presence carries the same meaning the column does rather than decorating it. Drawn in
          `--danger` because the lane was already that colour: the plan's rule is that the hue
          carries the meaning and the figure is chosen to agree with it, never the reverse.
          Cornered and behind nothing that has to be read.
        */}
        {d.lanes.overdue > 0 && (
          <img
            className="cdeck-persona"
            src="/char-overdue.webp"
            alt=""
            width={216}
            height={162}
            loading="lazy"
            aria-hidden="true"
          />
        )}
        <div className="cdeck-scroll">
          {/*
            The annunciators lead. They are the only thing on the board that answers "does
            anything need me" without him reading a number, which is how he uses this at 6am.
          */}
          <AnnunciatorStrip dashboard={d} />

          {/*
            The numbered cards from the mockup. Judgement questions come FIRST regardless of
            how many actions there are: a question only he can answer blocks work, where an
            action is work. Below the cards, the count that is not shown.
          */}
          <ol className="cdeck-signals">
            {d.proposals.slice(0, 2).map((q, i) => (
              <SignalCard
                key={`q${q.id}`}
                index={i + 1}
                kind="Judgement"
                tone="judge"
                title={q.title}
                /* `ref` is the note the question is about. Absent means the question exists
                   without a subject on record, which is worth saying rather than blanking. */
                meta={q.ref !== '' ? q.ref : 'no subject recorded'}
                provenance={q.kind}
                actions={[
                  {
                    label: 'Discuss',
                    icon: 'judgement',
                    onClick: () => openAsk({ lane: 'brain', about: q.title }),
                  },
                  { label: 'Resolve', icon: 'owed', onClick: () => void onResolve(q.id) },
                ]}
                onOpen={() =>
                  setDetail({
                    kind: 'Judgement',
                    tone: 'judge',
                    title: q.title,
                    meta: q.ref !== '' ? q.ref : 'no subject recorded',
                    provenance: q.kind,
                    facts: [
                      { label: 'Subject', value: q.ref },
                      { label: 'Raised', value: q.createdAt.slice(0, 10) },
                      { label: 'Kind', value: q.kind },
                    ],
                    actions: [
                      {
                        label: 'Discuss it',
                        icon: 'judgement',
                        hint: 'Ask MO about this',
                        onClick: () => openAsk({ lane: 'brain', about: q.title }),
                      },
                      {
                        label: 'Resolve',
                        icon: 'owed',
                        hint: 'Answered, take it off the queue',
                        onClick: () => void onResolve(q.id),
                      },
                    ],
                  })
                }
              />
            ))}
            {d.keyActions.slice(0, 3).map((a, i) => (
              <SignalCard
                key={a.path}
                index={Math.min(d.proposals.length, 2) + i + 1}
                kind={a.mine ? 'Action' : 'Follow up'}
                tone={a.lane === 'due' ? 'act' : 'soon'}
                title={a.text}
                meta={a.reason}
                provenance={a.mine ? 'you owe' : 'waiting'}
                actions={keyActionActions(a, reveal, onReload)}
                onOpen={() =>
                  setDetail({
                    kind: a.mine ? 'Action' : 'Follow up',
                    tone: a.lane === 'due' ? 'act' : 'soon',
                    title: a.text,
                    meta: a.reason,
                    provenance: a.mine ? 'you owe' : 'waiting on them',
                    facts: [
                      { label: 'Why now', value: a.reason },
                      { label: 'Lane', value: a.lane === 'due' ? 'has a deadline' : 'agreed recently' },
                      { label: 'Note', value: a.path },
                    ],
                    actions: [
                      ...(a.mine
                        ? [
                            {
                              label: 'Mark it done',
                              icon: 'owed' as const,
                              hint: 'Only status changes; nothing is lost',
                              onClick: () => void closeCommitment(a.path).then(onReload),
                            },
                          ]
                        : []),
                      {
                        label: 'Discuss it',
                        icon: 'judgement' as const,
                        hint: 'Ask MO what it knows',
                        onClick: () => openAsk({ lane: 'brain', about: a.text }),
                      },
                      {
                        label: 'Open the note',
                        icon: 'notes' as const,
                        hint: 'Read it in the vault',
                        onClick: () => reveal(a.path),
                      },
                    ],
                  })
                }
              />
            ))}
          </ol>

          {/* TWO, not three: this column already carries up to two judgement questions and three
              key actions, and eight cards is a list rather than a signal. The rest are in the
              Relationships module, and the remainder line below counts the ones hidden here. */}
          {chases.length > 0 && (
            <>
              {/*
                NAMED, where it was not before. These rows sat under the signal cards with no
                heading at all, so an account appeared in the column with nothing saying why it was
                there. "Bounties" is the rename and the fix at once: a chase IS the pursuit of
                someone who owes you, which is plainer than "chase lane" and the most natural word
                this register has.
              */}
              <p className="cdeck-listhead">
                <Icon name="waiting" size={13} />
                Bounties
                <span className="cdeck-listhead-note">{chases.length} owed to you</span>
              </p>
              <ul className="mo-chase-list">
                {/*
                  FOUR, NOT TWO. Two made sense when the queue was four accounts. Since the lane
                  widened it is around twenty, and he opened the deck looking for a drafted
                  follow-up and could not find one, because the row holding it was below the two
                  that fitted. The full list is on /mo; this is the shortlist, and a shortlist of
                  two is a shortlist that hides the thing it exists to surface.
                */}
                {chases.slice(0, 4).map((c) => (
                  <ChaseRow key={c.account} chase={c} onDone={onReload} />
                ))}
              </ul>
            </>
          )}

          {/* Absent when nothing is hidden, rather than "+0 more". */}
          {actions + decisions + chases.length >
            d.keyActions.slice(0, 3).length +
              Math.min(d.proposals.length, 2) +
              Math.min(chases.length, 2) && (
            <button type="button" className="cdeck-more" onClick={() => reveal('commitment')}>
              +{' '}
              {actions +
                decisions +
                chases.length -
                d.keyActions.slice(0, 3).length -
                Math.min(d.proposals.length, 2) -
                Math.min(chases.length, 2)}{' '}
              more items need your attention
            </button>
          )}

          {/* The full judgement queue keeps its own accept/reject controls; the cards above are
              a way in, not a replacement for answering one properly. */}
          <ProposalsBlock
            proposals={d.proposals}
            more={d.proposalsMore}
            onReject={(id) => void onResolve(id)}
            onAccepted={(id) => void onResolve(id)}
          />
        </div>
      </div>

      {/* ---- modules --------------------------------------------------- */}
      <div className="cdeck-col cdeck-col-modules">
        <p className="cdeck-coltitle cdeck-coltitle-blank" aria-hidden="true" />
        <div className="cdeck-scroll">
          <Panel
            id="deck-today"
            icon="agenda"
            hue="time"
            name="Today"
            caption="Timeline, meetings, tasks"
            headline={`${d.agenda.length} ${d.agenda.length === 1 ? 'event' : 'events'}`}
            sub={next === null ? 'nothing to prepare' : `next ${meetingWhenLabel(next)}`}
          >
            <AgendaList meetings={d.agenda} />
            <UpdateList updates={d.updates} />
            {d.daily !== undefined && <DailyBrief daily={d.daily} />}
          </Panel>

          <Panel
            id="deck-relationships"
            icon="waiting"
            hue="waiting"
            name="Relationships"
            caption="Accounts, people, interactions"
            /* Absent pipeline means the CRM was unreachable, which is not the same as
               having no accounts. Say so rather than printing a zero. */
            headline={
              d.pipeline === undefined
                ? 'not known'
                : `${d.pipeline.length} ${d.pipeline.length === 1 ? 'account' : 'accounts'}`
            }
            sub={
              d.pipeline === undefined
                ? 'the CRM did not answer'
                : `${accountsWaiting} waiting on you`
            }
            subTone={accountsWaiting > 0 ? 'warn' : undefined}
          >
            <PipelinePanel accounts={d.pipeline} chases={d.chases} onReload={onReload} />
            <UntouchedPanel />
          </Panel>

          <Panel
            id="deck-contracts"
            icon="owed"
            hue="action"
            /*
             * A commitment IS a contract, so nothing is lost and the register is gained. The
             * caption still says it in plain words underneath, which is how every rename in this
             * layer earns its place. The node type stays `commitment` and the notes stay in
             * mo/commitments/: Hermes, Hibiki and every skill address them by those names, so a
             * caption change must never reach the data.
             */
            name="Contracts"
            caption="Your promises and follow-ups"
            headline={`${d.owedTotal} owed`}
            sub={`${d.stuckTotal} stuck, ${d.waitingTotal} on others`}
            subTone={d.stuckTotal > 0 ? 'warn' : undefined}
          >
            <CommitmentList
              items={d.owed}
              total={d.owedTotal}
              lane="owed"
              empty="Nothing is owed by you right now."
              onClosed={onReload}
            />
            <StuckList items={d.stuck} />
            <CommitmentList
              items={d.waiting}
              total={d.waitingTotal}
              lane="waiting"
              empty="Nothing is outstanding from anyone else."
              onClosed={onReload}
            />
          </Panel>

          <Panel
            id="deck-operations"
            icon="action"
            hue="action"
            name="Operations"
            caption="Workload and execution"
            headline={`${commitments} live`}
            sub={`${d.lanes.overdue} overdue`}
            subTone={d.lanes.overdue > 0 ? 'crit' : undefined}
          >
            <WeeklyOpsPanel />
            <StackedBar
              slices={[
                { label: 'Due', value: d.lanes.due, token: '--accent' },
                { label: 'Live', value: d.lanes.live, token: '--success' },
                { label: 'Scheduled', value: d.lanes.scheduled, token: '--warning' },
                { label: 'Overdue', value: d.lanes.overdue, token: '--danger' },
                { label: 'Archived', value: d.lanes.archive, token: '--border-strong' },
              ]}
            />
          </Panel>

          <Panel
            id="deck-signals"
            icon="update"
            hue="time"
            name="Signals"
            caption="Changes, waiting, stuck"
            headline={`${decisions} to judge`}
            sub={`${d.updates.length} changed`}
          >
            <ChangesPanel />
            <GapsBlock gaps={d.gaps} />
          </Panel>

          <Panel
            id="deck-system"
            icon="agent"
            hue="health"
            name="System"
            caption="Automations and health"
            headline={`${(d.health ?? []).length} checks`}
            sub={d.limitations.length === 0 ? 'no gaps recorded' : `${d.limitations.length} gaps`}
            subTone={d.limitations.length === 0 ? 'ok' : 'warn'}
          >
            <HealthPanel rows={d.health ?? []} />
            <SocialPanel />
            <Telemetry telemetry={d.telemetry} lanes={d.lanes} holdings={d.holdings} />
          </Panel>

          {/*
            The prototype had a Projects module with progress bars. MO OS has no source for a
            percentage complete, so the module states what it is waiting for instead of drawing
            a bar. An invented bar on this surface would be the most damaging thing on it: it
            looks like the most confident element and would be the only fabricated one.
          */}
          <Panel
            id="deck-objectives"
            icon="objective"
            hue="time"
            name="Objectives"
            caption="What the work is for"
            headline={`${d.intent.length} tracked`}
            sub="no progress figure exists"
          >
            <IntentBlock objectives={d.intent} onSaved={onReload} />
            {/* Serves the objectives above rather than sitting in its own module: it is a thing
                that ACTS on them, and a separate panel would bury it. */}
            <BriefingButton />
            <p className="cdeck-nodata">
              Progress bars are not shown because nothing in the vault records how far along an
              objective is. Its open commitments and how long it has been quiet are counted above.
            </p>
          </Panel>
        </div>
      </div>

      {/* ---- right column ---------------------------------------------- */}
      <aside className="cdeck-col cdeck-col-side">
        <p className="cdeck-coltitle cdeck-coltitle-blank" aria-hidden="true" />
        <div className="cdeck-scroll">
          <NextMeeting
            meetings={d.agenda}
            owed={d.owed}
            /* Zou's `pre-meeting-briefing` is a real material kind on the server, so Prepare
               briefs rather than opening an empty box with the title in it. */
            onPrepare={(m) =>
              openAsk({ lane: 'zou', kind: 'pre-meeting-briefing', about: m.title })
            }
          />

          <Crew onManage={() => reveal('')} crew={d.telemetry.crew} />

          <section className="cdeck-card cdeck-know">
            <p className="cdeck-cardhead">
              <Icon name="graph" size={15} />
              The core
              <span className="cdeck-cardnote cdeck-cardnote-quiet">
                {whenLabel(d.generatedAt)}
              </span>
            </p>
            {/* The mockup leads with the count and the orb; the lane breakdown is under it,
                because "how big is the brain" and "which lane went quiet" are different
                questions and only the second one needs reading every day. */}
            <div className="cdeck-know-top">
              <div>
                <p className="cdeck-know-n">{d.holdings.totalNotes.toLocaleString()}</p>
                <p className="cdeck-know-l">Nodes</p>
                <button type="button" className="cdeck-ghost" onClick={() => reveal('')}>
                  Explore the brain
                  <Icon name="chevron" size={12} />
                </button>
              </div>
              {/*
                THE NUCLEUS. Driven by the vault's real per-kind counts rather than by a
                decorative loop, so the big bodies genuinely are what the vault is mostly made of.
                See mo/CoreNucleus.tsx: the panel's number and its picture now come from the same
                place, which is the only reason a moving graphic belongs on a board whose whole
                argument is that nothing on it is invented.
              */}
              <CoreNucleus counts={d.holdings.counts} />
            </div>
            <Brain
              total={d.holdings.totalNotes}
              inputs={[
                { label: 'Mail', count: d.telemetry.attentionByKind['commitment-due'] ?? 0, icon: 'inbox', target: '' },
                { label: 'Calendar', count: d.telemetry.meetings, icon: 'agenda', target: 'meeting' },
                { label: 'Meetings', count: d.telemetry.meetingsTranscribed, icon: 'clock', target: '' },
                { label: 'Vault', count: d.holdings.counts.person ?? 0, icon: 'notes', target: 'person' },
              ]}
              outputs={[
                { label: 'Actions', count: commitments, icon: 'action', target: 'commitment' },
                { label: 'Updates', count: d.updates.length, icon: 'update', target: '' },
                { label: 'Objectives', count: d.intent.length, icon: 'objective', target: 'objective' },
                { label: 'Questions', count: decisions, icon: 'judgement', target: '' },
              ]}
              onPick={(t) => {
                if (t !== '') reveal(t);
              }}
            />
          </section>

        </div>
      </aside>

      {/*
        SINCE YOU LAST CHECKED. Its own row, spanning the board above the bar, because it answers
        the question he opens the deck with and the columns answer the ones he opens it about.
        Renders nothing at all when nothing has changed; see mo/SinceYouChecked.tsx.
      */}
      <SinceYouChecked
        updates={d.updates}
        onOpen={(p) => reveal(p)}
        onViewAll={() => focusPanel('deck-signals')}
      />

      {/* ---- command bar ----------------------------------------------- */}
      <div className="cdeck-bar">
        {/*
          WHO, THEN WHAT. See mo/ChatBar.tsx: this replaces a wide field labelled "Ask MO OS
          anything..." whose Enter key ran a vault search, with two named chats that each go where
          they say. The overlay always had both lanes; the bar exposed neither.
        */}
        <ChatBar />
        <div className="cdeck-bar-right">
          {/* Dictation straight into the brain lane: what he says becomes the question, so the
              mic is a shortcut to the same overlay the chips open, not a separate feature. */}
          <Voice onHeard={(said) => openAsk({ lane: 'brain', about: said })} />
          {/* Beside the field it opens, rather than in the top corner where it crowded the
              system light and pushed the greeting off its own line. */}
          <ChatLauncher />
          <p className="cdeck-build" title="The build running in this browser">
            build {BUILD_ID}
          </p>
        </div>
      </div>

      {/* Last, so it lays over the board rather than inside a scrolling column. */}
      {detail !== null && <DeckDetail item={detail} onClose={() => setDetail(null)} />}
    </main>
  );
}

/** A top-bar control. `medal` is the gold one at the end of the row. */
function IconButton({
  label,
  icon,
  onClick,
  medal = false,
}: {
  label: string;
  icon: IconName;
  onClick: () => void;
  medal?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      className="cdeck-ico"
      data-medal={medal}
      title={label}
      aria-label={label}
      onClick={onClick}
    >
      <Icon name={icon} size={15} />
    </button>
  );
}

type SignalAction = { label: string; icon: IconName; onClick: () => void };

/**
 * One numbered card in the priority signal.
 *
 * The buttons are the reason it is a card and not a list row. His mockup puts the actions
 * ON the item, so answering does not mean finding the item again somewhere else, and the
 * grid is 2-across so a third action wraps under rather than shrinking the other two.
 *
 * `provenance` is always shown. It is the chip that says whether a line came from something
 * someone wrote or from something MO OS concluded, and on the card he reads first it is the
 * difference between a fact and a guess.
 */
function SignalCard({
  index,
  kind,
  tone,
  title,
  meta,
  provenance,
  actions,
  onOpen,
}: {
  index: number;
  kind: string;
  tone: 'act' | 'soon' | 'judge';
  title: string;
  meta: string;
  provenance: string;
  actions: SignalAction[];
  /** Opens the detail panel. The card clamps its title; the panel does not. */
  onOpen?: () => void;
}): JSX.Element {
  /*
   * The TEXT is the button, not the whole card. A card-wide click target would contain the
   * action buttons, and a button inside a button is invalid and behaves differently in every
   * browser. This also keeps the row reachable from the keyboard without stealing the tab
   * order from the actions, which are what he uses most.
   */
  const body = (
    <>
      <p className="cdeck-signal-no">
        <span className="cdeck-signal-dot" aria-hidden="true" />
        <span className="cdeck-signal-idx">{String(index).padStart(2, '0')}</span>
        {kind}
      </p>
      <p className="cdeck-signal-title">{title}</p>
      <p className="cdeck-signal-meta">
        {meta}
        <span className="cdeck-prov">{provenance}</span>
      </p>
    </>
  );

  return (
    <li className="cdeck-signal" data-tone={tone}>
      {onOpen === undefined ? (
        <div>{body}</div>
      ) : (
        <button type="button" className="cdeck-signal-open" onClick={onOpen}>
          {body}
        </button>
      )}
      <div className="cdeck-signal-actions">
        {actions.map((a) => (
          <button key={a.label} type="button" className="cdeck-signal-btn" onClick={a.onClick}>
            <Icon name={a.icon} size={14} />
            {a.label}
          </button>
        ))}
      </div>
    </li>
  );
}

/**
 * The three actions a key action earns.
 *
 * Done only when it is HIS: closing a commitment he is waiting on would mark someone else's
 * promise complete on his say-so, which is the one write on this board that cannot be undone
 * from the board.
 */
function keyActionActions(
  a: DashboardKeyAction,
  reveal: (t: string) => void,
  onReload: () => void,
): SignalAction[] {
  const base: SignalAction[] = [
    {
      label: 'Discuss',
      icon: 'judgement',
      onClick: () => openAsk({ lane: 'brain', about: a.text }),
    },
    { label: 'Open', icon: 'notes', onClick: () => reveal(a.path) },
  ];
  if (!a.mine) return base;
  return [
    ...base,
    {
      label: 'Done',
      icon: 'owed',
      onClick: () => {
        void closeCommitment(a.path).then(onReload);
      },
    },
  ];
}

/**
 * The mic, and it actually listens.
 *
 * The mockup has a mic button and MO OS has no speech input, so the honest options were to
 * draw a dead control or to make it real. This makes it real with the browser's own
 * `SpeechRecognition`, and RENDERS NOTHING where that does not exist rather than showing a
 * button that cannot work. The waveform animates only while listening: bars moving next to an
 * idle mic imply it is hearing him when it is not.
 */
function Voice({ onHeard }: { onHeard: (text: string) => void }): JSX.Element | null {
  const [listening, setListening] = useState(false);
  const recRef = useRef<{ start: () => void; stop: () => void } | null>(null);

  const Ctor = (globalThis as Record<string, unknown>)['SpeechRecognition'] ??
    (globalThis as Record<string, unknown>)['webkitSpeechRecognition'];

  useEffect(() => () => recRef.current?.stop(), []);

  const toggle = useCallback(() => {
    if (listening) {
      recRef.current?.stop();
      setListening(false);
      return;
    }
    if (typeof Ctor !== 'function') return;
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const rec = new (Ctor as any)();
    rec.lang = navigator.language;
    rec.interimResults = false;
    rec.onresult = (e: any) => {
      const said = String(e.results?.[0]?.[0]?.transcript ?? '').trim();
      if (said !== '') onHeard(said);
    };
    /* Ends and errors both stop the animation. A waveform still moving after the browser gave
       up is the failure mode this component exists to avoid. */
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    /* eslint-enable @typescript-eslint/no-explicit-any */
    recRef.current = rec;
    rec.start();
    setListening(true);
  }, [Ctor, listening, onHeard]);

  if (typeof Ctor !== 'function') return null;

  return (
    <span className="cdeck-voice">
      <span className="cdeck-wave" data-on={listening} aria-hidden="true">
        {Array.from({ length: 22 }, (_, i) => (
          <i key={i} style={{ animationDelay: `${(i % 7) * 90}ms` }} />
        ))}
      </span>
      <button
        type="button"
        className="cdeck-mic"
        data-on={listening}
        aria-pressed={listening}
        aria-label={listening ? 'Stop listening' : 'Speak to MO OS'}
        title={listening ? 'Stop listening' : 'Speak to MO OS'}
        onClick={toggle}
      >
        <Icon name="clock" size={18} />
      </button>
    </span>
  );
}

/** One hero number. `wide` is for the meeting slot, which is a phrase rather than a count. */
function HeroStat({
  tone,
  icon,
  value,
  label,
  note,
  onClick,
  wide = false,
}: {
  tone: 'judgement' | 'action' | 'time';
  icon: IconName;
  value: string;
  label: string;
  note: string;
  onClick: () => void;
  wide?: boolean;
}): JSX.Element {
  return (
    <button type="button" className="cdeck-stat" data-tone={tone} onClick={onClick}>
      <span className="cdeck-stat-ring">
        <Icon name={icon} size={16} />
      </span>
      <span className="cdeck-stat-text">
        <span className="cdeck-stat-value" data-wide={wide}>
          {value}
        </span>
        <span className="cdeck-stat-label">{label}</span>
        <span className="cdeck-stat-note">{note}</span>
      </span>
    </button>
  );
}

/**
 * The core map's nodes. A FIXED table, not `Math.random()`: the panel is glanced at every morning
 * and a constellation that reshuffles on every render is a panel that can never be recognised.
 * Placed on and just off the three ellipses above, brighter near the centre so the map reads as a
 * body with a core rather than as scattered dots.
 */
const ORBIT_NODES: readonly { x: number; y: number; r: number; o: number }[] = [
  { x: 12, y: 46, r: 1.6, o: 0.5 }, { x: 26, y: 34, r: 1.2, o: 0.38 },
  { x: 34, y: 58, r: 2.0, o: 0.62 }, { x: 46, y: 26, r: 1.3, o: 0.42 },
  { x: 52, y: 68, r: 1.5, o: 0.5 }, { x: 74, y: 30, r: 1.8, o: 0.58 },
  { x: 88, y: 54, r: 1.3, o: 0.44 }, { x: 104, y: 44, r: 1.6, o: 0.48 },
  { x: 66, y: 74, r: 1.2, o: 0.36 }, { x: 96, y: 68, r: 1.4, o: 0.4 },
  { x: 22, y: 66, r: 1.1, o: 0.32 }, { x: 82, y: 18, r: 1.1, o: 0.3 },
];

/**
 * A context module: closed by default, opened on purpose.
 *
 * `<details>` rather than state, so the browser owns the disclosure and it survives without
 * JavaScript. The header carries a headline and a sub-line because a single count cannot say
 * both "how much is here" and "how much of it is wrong", which is the pair he actually reads.
 */
/**
 * The crew, as the five portraits from the mockup.
 *
 * The same roster the tracking tower's panel carries, and the same reason it lists five: Zou
 * is built and was missing, and "not built" was covering three different states.
 */
/*
 * THE FALLBACK ONLY, and every entry here is deliberately unlive.
 *
 * The roster is the server's now (`telemetry.crew`, see mo/crew.ts), because this const used to be
 * the roster and `live: true` was a claim rather than a measurement: it showed Hermes active on
 * every one of the nine days its cron scheduler was dead. What remains is what to draw when an
 * older server sends no crew at all, and in that case the honest answer about every agent is that
 * we do not know, so nothing here claims to be running.
 */
export const CREW_FALLBACK: CrewRow[] = [
  { name: 'Fawzy', role: 'Capture & automate', state: 'planned', plate: 'hermes', detail: 'state not reported' },
  { name: 'Zou', role: 'Create & compose', state: 'planned', plate: 'zou', detail: 'state not reported', mark: '象' },
  { name: 'Hibiki', role: 'Brand & content', state: 'planned', plate: 'hibiki', detail: 'state not reported', mark: '響' },
  { name: 'Kantoku', role: 'Personal brand', state: 'planned', plate: 'kantoku', detail: 'state not reported', mark: '監督' },
  { name: 'Satori', role: 'External intel', state: 'planned', plate: 'research', detail: 'state not reported', mark: '悟' },
  { name: 'Kura', role: 'Financial intel', state: 'planned', plate: 'cfo', detail: 'state not reported', mark: '蔵' },
];

export interface CrewRow {
  name: string;
  role: string;
  plate: string;
  state: 'active' | 'late' | 'planned' | 'not built';
  detail: string;
  /** Kanji for the name. Fawzy has none: Hermes is the platform, not a Japanese name. */
  mark?: string;
}

/** Live is a measured state, never a roster entry. `late` is emphatically not live. */
export const isLive = (state: CrewRow['state']): boolean => state === 'active';

/**
 * Choose between the three surfaces.
 *
 * The current one is listed and disabled rather than omitted. Omitting it is what made the old
 * control read as "go elsewhere" instead of "you are here, and here is what else there is".
 */
export function ViewPicker({
  current,
  onPick,
}: {
  current: DeckView;
  onPick: (v: DeckView) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const openView = useApp((st) => st.openView);
  const rows: { key: DeckView | 'mo'; label: string; note: string; icon: IconName; go: () => void }[] = [
    ...VIEW_ROWS.map((r) => ({ key: r.key, label: r.label, note: r.note, icon: r.icon, go: () => onPick(r.key) })),
    { key: 'mo', label: 'The archives', note: 'ask and browse', icon: 'notes', go: () => openView({ kind: 'mo' }) },
  ];
  return (
    <div className="cdeck-viewpick">
      <button
        type="button"
        className="cdeck-switch"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        <Icon name="chevron" size={14} />
        <span>Switch view</span>
      </button>
      {open && (
        <ul className="cdeck-viewmenu" role="menu">
          {rows.map((r) => {
            const here = r.key === current;
            return (
              <li key={r.key} role="none">
                <button
                  type="button"
                  role="menuitem"
                  className="cdeck-viewopt"
                  data-here={here}
                  disabled={here}
                  onClick={() => {
                    setOpen(false);
                    r.go();
                  }}
                >
                  <Icon name={r.icon} size={13} />
                  <span className="cdeck-viewopt-name">{r.label}</span>
                  <span className="cdeck-viewopt-note">{here ? 'you are here' : r.note}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Crew({ onManage, crew }: { onManage: () => void; crew?: CrewRow[] }): JSX.Element {
  const rows = crew !== undefined && crew.length > 0 ? crew : CREW_FALLBACK;
  const live = rows.filter((a) => isLive(a.state)).length;
  const lateCount = rows.filter((a) => a.state === 'late').length;
  return (
    <section className="cdeck-card" id="deck-crew">
      <p className="cdeck-cardhead">
        <Icon name="agent" size={15} />
        The crew
        {/*
          A silent agent is the headline, not a footnote. "2 active" beside a dead Hermes is the
          sentence that let a nine-day outage pass unnoticed every morning.
        */}
        <span className="cdeck-cardnote" data-alarm={lateCount > 0}>
          {lateCount > 0 ? `${lateCount} silent` : `${live} active`}
        </span>
      </p>
      <ul className="cdeck-crew">
        {rows.map((a) => (
          <li
            key={a.name}
            className="cdeck-crew-member"
            data-live={isLive(a.state)}
            data-state={a.state}
            title={a.detail}
          >
            <span className="cdeck-crew-plate">
              <img src={`/crew-${a.plate}.webp`} alt="" width={216} height={162} loading="lazy" />
            </span>
            <span className="cdeck-crew-name">
              {a.name}
              {/* The kanji as a quiet mark, not a decoration: it is the name, in its own script. */}
              {a.mark !== undefined && <span className="cdeck-crew-mark" aria-hidden="true">{a.mark}</span>}
            </span>
            <span className="cdeck-crew-role">{a.role}</span>
            <span className="cdeck-crew-state">
              <span className="cdeck-crew-dot" aria-hidden="true" />
              {a.state === 'late' ? a.detail : a.state}
            </span>
          </li>
        ))}
      </ul>
      <button type="button" className="cdeck-manage" onClick={onManage}>
        Manage agents
        <Icon name="chevron" size={13} />
      </button>
    </section>
  );
}

/** Green only when the limitations list is empty, and it says how many rather than only amber. */
function SystemLight({ limitations }: { limitations: string[] }): JSX.Element {
  const n = limitations.length;
  const ok = n === 0;
  return (
    <p className="cdeck-light" data-ok={ok}>
      <span className="cdeck-light-line">
        <span className="cdeck-light-dot" aria-hidden="true" />
        {ok ? 'System nominal' : `${n} known gap${n === 1 ? '' : 's'}`}
      </span>
      <span className="cdeck-light-note">
        {ok ? 'every lane reported cleanly' : 'listed inside System'}
      </span>
    </p>
  );
}

/**
 * A short "when" for the hero and the module headers.
 *
 * Deliberately not `meetingWhen` from the tower: that one is written for a list row and says
 * "12:00" for today, which reads as a bare number with no date beside it in a sentence like
 * "Next up 12:00". This adds the word the sentence needs and nothing else.
 */
function meetingWhenLabel(m: Dashboard['agenda'][number]): string {
  const t = Date.parse(m.startsAt);
  if (m.allDay) return m.isToday ? 'today, all day' : `${m.startsAt.slice(0, 10)}, all day`;
  if (Number.isNaN(t)) return m.isToday ? 'today' : m.startsAt.slice(0, 10);
  const d = new Date(t);
  const time = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  if (m.isToday) return `at ${time}`;
  const day = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${day}, ${time}`;
}
